import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { AxiosError } from 'axios';
import { AdminCaseTableItem } from 'src/app/components/dashboard/cases/cases.component';
import {
  ClaimsService,
  GetHiddenClaimsResponse,
  PayeeFeeClaim,
  ProceduralFeeClaim,
} from 'src/app/services/claims/claims.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import { errorResponse } from 'src/types/error-response';
import { PayeePopupComponent } from '../../shared/payee-popup/payee-popup.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { DecimalPipe, KeyValuePipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeButtonComponent } from '../../shared/payee-buttons/payee-button/payee-button.component';

@Component({
  selector: 'app-edit-hidden-claims-dialog',
  templateUrl: './edit-hidden-claims-dialog.component.html',
  styleUrls: ['./edit-hidden-claims-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    MatCheckbox,
    MatProgressSpinner,
    PayeeButtonComponent,
    DecimalPipe,
    KeyValuePipe,
  ],
})
export class EditHiddenClaimsDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<EditHiddenClaimsDialogComponent>>(MatDialogRef);
  private claimsService = inject(ClaimsService);
  private payeeCasesService = inject(PayeeCasesService);
  private fb = inject(FormBuilder);
  private helpersService = inject(HelpersService);

  data = inject<AdminCaseTableItem>(MAT_DIALOG_DATA);

  readonly form = this.fb.group({
    interest_claim_added: this.fb.control(true, { nonNullable: true }),
    payee_fee_claim_added: this.fb.control(true, { nonNullable: true }),
    procedural_fee_claim_added: this.fb.control(true, { nonNullable: true }),
    flat_rate_cost_claims_added: this.fb.control(true, { nonNullable: true }),
  });

  readonly loading = signal(false);
  readonly submitting = signal(false);

  claimSums: GetHiddenClaimsResponse['payee_case']['principal_claims_sums'] =
    {};
  payeeFeeClaim: PayeeFeeClaim | null = null;
  proceduralFeeClaim: ProceduralFeeClaim | null = null;

  get interest_claim_added() {
    return this.form.controls.interest_claim_added;
  }
  get payee_fee_claim_added() {
    return this.form.controls.payee_fee_claim_added;
  }
  get procedural_fee_claim_added() {
    return this.form.controls.procedural_fee_claim_added;
  }
  get flat_rate_cost_claims_added() {
    return this.form.controls.flat_rate_cost_claims_added;
  }

  async ngOnInit() {
    try {
      this.loading.set(true);
      const result = await this.claimsService.getHiddenClaims(this.data.id);

      this.claimSums = result.payee_case.principal_claims_sums;
      this.payeeFeeClaim = result.payee_fee_claim;
      this.proceduralFeeClaim = result.procedural_fee_claim;

      this.interest_claim_added.patchValue(result.interest_claim_added);
      this.flat_rate_cost_claims_added.patchValue(
        result.flat_rate_cost_claims_added,
      );
      this.payee_fee_claim_added.patchValue(result.payee_fee_claim_added);
      this.procedural_fee_claim_added.patchValue(
        result.procedural_fee_claim_added,
      );
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error', error.response?.data);
        this.cancel();
      } else {
        console.error('Unknown error', error);
        this.cancel();
      }
    } finally {
      this.loading.set(false);
    }
  }

  readonly submit = async (): Promise<void> => {
    if (this.submitting()) {
      return;
    }

    if (this.form.invalid || this.loading()) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    try {
      await this.performSave();
      this.payeeCasesService.setCasesSubject.next();
      this.dialogRef.close(true);
    } catch (error: AxiosError | unknown) {
      this.handleSaveError(error);
    }
  };

  readonly cancel = (): void => {
    if (this.submitting()) {
      return;
    }

    this.dialogRef.close(false);
  };

  private async performSave(): Promise<void> {
    this.submitting.set(true);

    try {
      const {
        interest_claim_added,
        flat_rate_cost_claims_added,
        payee_fee_claim_added,
        procedural_fee_claim_added,
      } = this.form.getRawValue();

      await this.claimsService.updateHiddenClaims(this.data.id, {
        interest_claim_added,
        flat_rate_cost_claims_added,
        payee_fee_claim_added,
        procedural_fee_claim_added,
      });
    } finally {
      this.submitting.set(false);
    }
  }

  private handleSaveError(error: AxiosError | unknown): void {
    if (error instanceof AxiosError) {
      const errorData = errorResponse.safeParse(error.response?.data);
      if (errorData.success) {
        for (const [path, errors] of Object.entries(errorData.data.errors)) {
          const control = this.form.get(path);
          if (!control) {
            console.warn('Control not found');
            this.form.setErrors({
              unknown: errors[0],
            });
            continue;
          }

          setTimeout(() => {
            control.setErrors(
              {
                unknown: errors[0],
              },
              {
                emitEvent: false,
              },
            );
            control.markAsDirty();
          });
        }
      } else {
        console.error('Network error', error.response?.data);
      }
    } else {
      console.error('Unknown error', error);
    }
  }
}
